import axios from 'axios'
import { store } from '../store'
import { extractErrorMessage } from './responseAdapter'

const apiClient = axios.create({
  baseURL: (import.meta.env?.VITE_API_BASE_URL as string) || '/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// 登录/注册等无需 token 的路径，不打印“未找到token”警告
const NO_TOKEN_URLS = [
  '/v1/auth/login',
  '/v1/auth/register',
  '/v1/auth/roles/available',
  '/auth/login',
  '/auth/register',
]

// 请求拦截：附加 Token、处理 FormData
function attachAuthToRequest(config: import('axios').InternalAxiosRequestConfig) {
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type']
  }
  const url = config.url ?? ''
  const needsToken = !NO_TOKEN_URLS.some((path) => url.includes(path))

  if (needsToken) {
    const state = store.getState()
    let token = state.auth.token
    if (!token) {
      token = localStorage.getItem('token')
    }
    if (token) {
      const tokenStr = typeof token === 'string' ? token : String(token)
      config.headers.Authorization = `Bearer ${tokenStr}`
    } else {
      console.warn('[API Client] 未找到token:', {
        url: config.url,
        reduxToken: !!store.getState().auth.token,
        localStorageToken: !!localStorage.getItem('token'),
      })
    }
  }
  return config
}

apiClient.interceptors.request.use(attachAuthToRequest, (error) => Promise.reject(error))

// 响应拦截器
apiClient.interceptors.response.use(
  (response) => {
    // axios 的 response 对象包含 data 属性
    // 直接返回 response.data，这样调用方可以直接使用数据
    return response.data
  },
  (error) => {
    console.error('[API Client] 请求错误:', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      message: error.message,
      fullError: error
    })
    // 如果是教材编写相关的错误，输出更详细的信息
    if (error.config?.url?.includes('/textbooks/writing')) {
      console.error('[API Client] 教材编写API错误详情:', {
        url: error.config?.url,
        status: error.response?.status,
        responseData: error.response?.data,
        errorMessage: error.message
      })
    }
    
    // 401错误处理：只在非角色管理相关API时自动跳转
    // 避免在角色管理页面加载时因权限问题自动跳转
    if (error.response?.status === 401) {
      const url = error.config?.url || ''
      // 角色管理相关的API失败时不自动跳转，让组件自己处理
      const isRoleManagementAPI = url.includes('/roles') || url.includes('/user/') && url.includes('/roles')
      
      if (!isRoleManagementAPI) {
        // 非角色管理API的401错误，清除认证状态并跳转
        store.dispatch({ type: 'auth/logout' })
        window.location.href = '/auth/login'
      }
      // 角色管理API的401错误，只清除认证状态，不跳转（让组件处理）
    }
    
    // 保留完整的错误信息，包括响应数据
    // AjaxResult 的格式是 { code, msg, data }
    const errorData = error.response?.data || {}
    const enhancedError = {
      ...error,
      msg: errorData.msg || errorData.message,
      message: extractErrorMessage(error),
      code: errorData.code,
      data: errorData.data,
      response: error.response
    }
    return Promise.reject(enhancedError)
  }
)

export default apiClient

/** 保留完整 AxiosResponse（如 blob 下载） */
export const rawApiClient = axios.create({
  baseURL: (import.meta.env?.VITE_API_BASE_URL as string) || '/api',
  timeout: 300000,
})

rawApiClient.interceptors.request.use(attachAuthToRequest, (error) => Promise.reject(error))

